// Season-specific makeup and hair color recommendations
// Each color includes hex code for visual swatches

export type ColorSwatch = {
  name: string;
  hex: string;
};

export type MakeupPalette = {
  lipsticks: ColorSwatch[];
  eyeshadows: ColorSwatch[];
  blushes: ColorSwatch[];
  bronzers: ColorSwatch[];
  metals: {
    best: ColorSwatch[];
    avoid: ColorSwatch[];
  };
};

export type HairColorGuide = {
  ideal: ColorSwatch[];
  canWork: ColorSwatch[];
  avoid: ColorSwatch[];
  highlights: ColorSwatch[];
  lowlights: ColorSwatch[];
};

export type SeasonBeautyGuide = {
  makeup: MakeupPalette;
  hair: HairColorGuide;
  tips: string[];
};

export const beautyGuideData: Record<string, Record<string, SeasonBeautyGuide>> = {
  SPRING: {
    LIGHT: {
      makeup: {
        lipsticks: [
          { name: "Peachy Pink", hex: "#FFACA8" },
          { name: "Light Coral", hex: "#F08080" },
          { name: "Warm Nude", hex: "#D4A589" },
          { name: "Soft Apricot", hex: "#FBCEB1" },
          { name: "Warm Rose", hex: "#E8ADAA" },
        ],
        eyeshadows: [
          { name: "Champagne", hex: "#F7E7CE" },
          { name: "Warm Taupe", hex: "#C4AEAD" },
          { name: "Soft Peach", hex: "#FFE5D9" },
          { name: "Light Bronze", hex: "#C9AE5D" },
          { name: "Warm Brown", hex: "#A67B5B" },
          { name: "Soft Gold", hex: "#E8D4A2" },
        ],
        blushes: [
          { name: "Peach", hex: "#FFCBA4" },
          { name: "Light Coral", hex: "#F4A4A4" },
          { name: "Warm Pink", hex: "#E8A9A9" },
        ],
        bronzers: [
          { name: "Light Golden", hex: "#D4A574" },
          { name: "Warm Beige", hex: "#C8A676" },
        ],
        metals: {
          best: [
            { name: "Light Gold", hex: "#FFD700" },
            { name: "Rose Gold", hex: "#E8B4B8" },
            { name: "Warm Brass", hex: "#CD9575" },
          ],
          avoid: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Platinum", hex: "#E5E4E2" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Golden Blonde", hex: "#E8D4A2" },
          { name: "Warm Honey", hex: "#D4A84B" },
          { name: "Strawberry Blonde", hex: "#D4956A" },
          { name: "Light Copper", hex: "#C67C4E" },
        ],
        canWork: [
          { name: "Light Warm Brown", hex: "#A67B5B" },
          { name: "Caramel", hex: "#C68E5A" },
          { name: "Soft Ginger", hex: "#C4824A" },
        ],
        avoid: [
          { name: "Ash Blonde", hex: "#B8A99A" },
          { name: "Cool Brown", hex: "#694F3A" },
          { name: "Black", hex: "#1C1C1C" },
          { name: "Platinum", hex: "#E8E4E1" },
        ],
        highlights: [
          { name: "Buttery Blonde", hex: "#F5DEB3" },
          { name: "Golden", hex: "#FFD700" },
        ],
        lowlights: [
          { name: "Warm Toffee", hex: "#9A7B4F" },
          { name: "Light Auburn", hex: "#A45A3D" },
        ],
      },
      tips: [
        "Stick to warm, light-reflecting makeup - nothing too heavy or matte",
        "Peachy and coral tones will brighten your complexion more than cool pinks",
        "For a natural look, match your blush to the inside of your lip",
        "Avoid stark white - opt for ivory or cream instead",
        "Your best metals are light gold and rose gold",
      ],
    },
    TRUE: {
      makeup: {
        lipsticks: [
          { name: "True Coral", hex: "#FF6F61" },
          { name: "Warm Poppy", hex: "#E35335" },
          { name: "Tangerine", hex: "#FF9966" },
          { name: "Warm Red", hex: "#E34234" },
          { name: "Peachy Nude", hex: "#E8B89D" },
        ],
        eyeshadows: [
          { name: "Warm Gold", hex: "#D4AF37" },
          { name: "Bronze", hex: "#CD7F32" },
          { name: "Terracotta", hex: "#C67450" },
          { name: "Teal", hex: "#367588" },
          { name: "Warm Green", hex: "#6B8E4E" },
          { name: "Copper", hex: "#B87333" },
        ],
        blushes: [
          { name: "Coral", hex: "#FF7F50" },
          { name: "Warm Peach", hex: "#FFBE9F" },
          { name: "Apricot", hex: "#FDD5B1" },
        ],
        bronzers: [
          { name: "Golden Bronze", hex: "#C48A47" },
          { name: "Warm Tan", hex: "#B5835A" },
        ],
        metals: {
          best: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Brass", hex: "#B5A642" },
            { name: "Copper", hex: "#B87333" },
          ],
          avoid: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "White Gold", hex: "#E8E4E1" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Warm Golden Brown", hex: "#996515" },
          { name: "Copper", hex: "#B87333" },
          { name: "Warm Auburn", hex: "#A52A2A" },
          { name: "Golden Blonde", hex: "#DAA520" },
        ],
        canWork: [
          { name: "Warm Red", hex: "#B7410E" },
          { name: "Honey Brown", hex: "#B5651D" },
          { name: "Caramel", hex: "#C68E5A" },
        ],
        avoid: [
          { name: "Ash Tones", hex: "#A89F91" },
          { name: "Blue-Black", hex: "#1C2841" },
          { name: "Platinum", hex: "#E8E4E1" },
          { name: "Burgundy", hex: "#722F37" },
        ],
        highlights: [
          { name: "Golden Blonde", hex: "#F0D77B" },
          { name: "Copper Streaks", hex: "#CE7942" },
        ],
        lowlights: [
          { name: "Warm Brown", hex: "#8B5A2B" },
          { name: "Auburn", hex: "#8B4513" },
        ],
      },
      tips: [
        "Go bold with warm colors - you can handle saturation better than other Springs",
        "Coral is your power color for lips and cheeks",
        "Warm metallics in gold and bronze make your eyes pop",
        "Turquoise and teal eyeshadows complement your warmth beautifully",
        "Avoid anything with a grey or ashy undertone",
      ],
    },
    BRIGHT: {
      makeup: {
        lipsticks: [
          { name: "Bright Coral", hex: "#FF6347" },
          { name: "Warm Fuchsia", hex: "#E55B8A" },
          { name: "Clear Red", hex: "#ED2939" },
          { name: "Bright Peach", hex: "#FF8C69" },
          { name: "Warm Pink", hex: "#FF69B4" },
        ],
        eyeshadows: [
          { name: "Bright Gold", hex: "#FFD700" },
          { name: "Turquoise", hex: "#40E0D0" },
          { name: "Bright Teal", hex: "#008B8B" },
          { name: "Warm Purple", hex: "#9B59B6" },
          { name: "Lime Shimmer", hex: "#BFFF00" },
          { name: "Electric Blue", hex: "#7DF9FF" },
        ],
        blushes: [
          { name: "Bright Coral", hex: "#FF6B6B" },
          { name: "Hot Pink", hex: "#FF69B4" },
          { name: "Warm Peach", hex: "#FFB07C" },
        ],
        bronzers: [
          { name: "Warm Golden", hex: "#C9A86C" },
          { name: "Light Bronze", hex: "#CD853F" },
        ],
        metals: {
          best: [
            { name: "Bright Gold", hex: "#FFD700" },
            { name: "Rose Gold", hex: "#E8B4B8" },
            { name: "Polished Brass", hex: "#CFB53B" },
          ],
          avoid: [
            { name: "Antique Silver", hex: "#A8A9AD" },
            { name: "Pewter", hex: "#8E9191" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Bright Copper", hex: "#DA8A67" },
          { name: "Warm Golden Blonde", hex: "#E0C068" },
          { name: "Clear Auburn", hex: "#B55239" },
          { name: "Bright Ginger", hex: "#C65D3B" },
        ],
        canWork: [
          { name: "Warm Chestnut", hex: "#8B4513" },
          { name: "Bright Caramel", hex: "#D2691E" },
          { name: "Rich Copper", hex: "#A0522D" },
        ],
        avoid: [
          { name: "Mousy Brown", hex: "#7B6C57" },
          { name: "Ash Anything", hex: "#A89F91" },
          { name: "Muted Tones", hex: "#9C8B75" },
          { name: "Cool Black", hex: "#1C1C1C" },
        ],
        highlights: [
          { name: "Bright Gold", hex: "#FFD54F" },
          { name: "Sunny Blonde", hex: "#F8DE7E" },
        ],
        lowlights: [
          { name: "Warm Copper", hex: "#B56727" },
          { name: "Bright Auburn", hex: "#A0422D" },
        ],
      },
      tips: [
        "You can wear the brightest, most saturated warm colors - embrace them!",
        "Avoid muted or dusty shades - they'll make you look tired",
        "Clear, bright lip colors in coral and warm pink are your best friends",
        "Don't be afraid of bold eye looks - you can pull off turquoise and bright gold",
        "Your jewelry should be polished and bright, not antique or matte",
      ],
    },
  },
  SUMMER: {
    LIGHT: {
      makeup: {
        lipsticks: [
          { name: "Soft Pink", hex: "#E8B4B8" },
          { name: "Dusty Rose", hex: "#D4A5A5" },
          { name: "Light Mauve", hex: "#C9A9C9" },
          { name: "Cool Nude", hex: "#C8A696" },
          { name: "Soft Berry", hex: "#C9A0C9" },
        ],
        eyeshadows: [
          { name: "Soft Lavender", hex: "#D8BFD8" },
          { name: "Cool Taupe", hex: "#B8A99A" },
          { name: "Dusty Pink", hex: "#D4A5A5" },
          { name: "Soft Grey", hex: "#C0C0C0" },
          { name: "Powder Blue", hex: "#B0C4DE" },
          { name: "Muted Mauve", hex: "#C9A9C9" },
        ],
        blushes: [
          { name: "Soft Pink", hex: "#E8B4B8" },
          { name: "Light Rose", hex: "#E4B4C2" },
          { name: "Cool Peach", hex: "#E8C4B8" },
        ],
        bronzers: [
          { name: "Cool Taupe", hex: "#A69280" },
          { name: "Soft Contour", hex: "#B8A090" },
        ],
        metals: {
          best: [
            { name: "Rose Gold", hex: "#E8B4B8" },
            { name: "Soft Silver", hex: "#D4D4D4" },
            { name: "White Gold", hex: "#E8E4E1" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Brass", hex: "#B5A642" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Ash Blonde", hex: "#C8B79E" },
          { name: "Light Ash Brown", hex: "#A69F8F" },
          { name: "Cool Blonde", hex: "#D4C8B8" },
          { name: "Soft Mushroom", hex: "#A8998A" },
        ],
        canWork: [
          { name: "Platinum Blonde", hex: "#E8E4E1" },
          { name: "Cool Light Brown", hex: "#9A8878" },
          { name: "Soft Bronde", hex: "#B8A890" },
        ],
        avoid: [
          { name: "Golden Blonde", hex: "#E8D4A2" },
          { name: "Warm Auburn", hex: "#A52A2A" },
          { name: "Copper", hex: "#B87333" },
          { name: "Jet Black", hex: "#1C1C1C" },
        ],
        highlights: [
          { name: "Cool Blonde", hex: "#E0D8C8" },
          { name: "Ash Highlights", hex: "#C8C0B0" },
        ],
        lowlights: [
          { name: "Soft Ash Brown", hex: "#8A8074" },
          { name: "Cool Taupe", hex: "#9A9080" },
        ],
      },
      tips: [
        "Keep everything soft and muted - avoid anything too bright or harsh",
        "Cool-toned pinks and mauves are more flattering than warm corals",
        "Your best look is effortless and delicate, not dramatic",
        "Rose gold jewelry complements your coloring beautifully",
        "Avoid heavy bronzer - a soft cool-toned contour is better",
      ],
    },
    TRUE: {
      makeup: {
        lipsticks: [
          { name: "Dusty Rose", hex: "#C9A0A0" },
          { name: "Mauve", hex: "#B08B8B" },
          { name: "Soft Raspberry", hex: "#B76E79" },
          { name: "Muted Berry", hex: "#8E6278" },
          { name: "Rose Brown", hex: "#A68B7C" },
        ],
        eyeshadows: [
          { name: "Soft Plum", hex: "#9B8B9B" },
          { name: "Dusty Blue", hex: "#8A9AAA" },
          { name: "Cool Brown", hex: "#8B7B6B" },
          { name: "Muted Teal", hex: "#6B8B8B" },
          { name: "Soft Grey", hex: "#A8A8A8" },
          { name: "Mauve", hex: "#9B8090" },
        ],
        blushes: [
          { name: "Dusty Pink", hex: "#C9A0A0" },
          { name: "Soft Mauve", hex: "#B8A0A8" },
          { name: "Muted Rose", hex: "#B89898" },
        ],
        bronzers: [
          { name: "Soft Taupe", hex: "#9A8A7A" },
          { name: "Cool Bronze", hex: "#8A7A6A" },
        ],
        metals: {
          best: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Rose Gold", hex: "#C9A0A0" },
            { name: "White Gold", hex: "#E0DCD8" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Copper", hex: "#B87333" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Cool Brown", hex: "#6B5A4A" },
          { name: "Ash Brown", hex: "#8A7B6B" },
          { name: "Soft Brunette", hex: "#5A4A3A" },
          { name: "Mushroom Brown", hex: "#8B8070" },
        ],
        canWork: [
          { name: "Cool Dark Blonde", hex: "#A89B8B" },
          { name: "Soft Black", hex: "#2C2824" },
          { name: "Muted Auburn", hex: "#7B4A3A" },
        ],
        avoid: [
          { name: "Golden Blonde", hex: "#E8D4A2" },
          { name: "Bright Copper", hex: "#DA8A67" },
          { name: "Warm Caramel", hex: "#C68E5A" },
          { name: "Orange-Red", hex: "#C65D3B" },
        ],
        highlights: [
          { name: "Ash Blonde", hex: "#C0B0A0" },
          { name: "Cool Caramel", hex: "#A89080" },
        ],
        lowlights: [
          { name: "Soft Espresso", hex: "#4A3A2A" },
          { name: "Cool Brown", hex: "#5A4A3A" },
        ],
      },
      tips: [
        "Embrace muted, sophisticated colors - nothing too bright or too dark",
        "Dusty rose and mauve are your signature lip colors",
        "Cool brown eyeshadows are more flattering than warm browns",
        "Silver and rose gold jewelry both work well for you",
        "Avoid anything with obvious orange or yellow undertones",
      ],
    },
    COOL: {
      makeup: {
        lipsticks: [
          { name: "Cool Pink", hex: "#DE8CA5" },
          { name: "Raspberry", hex: "#B55580" },
          { name: "Soft Fuchsia", hex: "#C55A8A" },
          { name: "Berry", hex: "#8B4570" },
          { name: "Cool Rose", hex: "#C89BA0" },
        ],
        eyeshadows: [
          { name: "Cool Grey", hex: "#9A9AAA" },
          { name: "Soft Navy", hex: "#5A6A8A" },
          { name: "Lavender", hex: "#B8A8C8" },
          { name: "Icy Pink", hex: "#E8C8D8" },
          { name: "Slate", hex: "#7A8A9A" },
          { name: "Soft Plum", hex: "#8A7090" },
        ],
        blushes: [
          { name: "Cool Pink", hex: "#D8A0B0" },
          { name: "Soft Berry", hex: "#C890A0" },
          { name: "Icy Rose", hex: "#E8B8C8" },
        ],
        bronzers: [
          { name: "Cool Contour", hex: "#9A8A8A" },
          { name: "Soft Taupe", hex: "#A89898" },
        ],
        metals: {
          best: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Platinum", hex: "#E5E4E2" },
            { name: "White Gold", hex: "#E8E4E1" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Brass", hex: "#B5A642" },
            { name: "Copper", hex: "#B87333" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Ash Blonde", hex: "#B8A898" },
          { name: "Cool Brown", hex: "#6A5A4A" },
          { name: "Icy Blonde", hex: "#E0D8D0" },
          { name: "Soft Ash", hex: "#9A908A" },
        ],
        canWork: [
          { name: "Platinum", hex: "#E8E4E1" },
          { name: "Cool Black", hex: "#1C1C1C" },
          { name: "Silver Grey", hex: "#A8A8A8" },
        ],
        avoid: [
          { name: "Golden Tones", hex: "#DAA520" },
          { name: "Warm Brown", hex: "#8B5A2B" },
          { name: "Copper", hex: "#B87333" },
          { name: "Caramel", hex: "#C68E5A" },
        ],
        highlights: [
          { name: "Icy Blonde", hex: "#E8E0D8" },
          { name: "Cool Ash", hex: "#C8C0B8" },
        ],
        lowlights: [
          { name: "Cool Espresso", hex: "#3A3030" },
          { name: "Ash Brown", hex: "#6A5A50" },
        ],
      },
      tips: [
        "Cool, clear colors with blue undertones are your best friends",
        "Silver jewelry will always look better on you than gold",
        "Avoid anything with warm or yellow undertones",
        "You can wear soft brights - just keep them cool-toned",
        "Raspberry and berry lip colors are particularly flattering",
      ],
    },
  },
  AUTUMN: {
    SOFT: {
      makeup: {
        lipsticks: [
          { name: "Dusty Peach", hex: "#D4A584" },
          { name: "Soft Terracotta", hex: "#C4907A" },
          { name: "Muted Coral", hex: "#C99A8A" },
          { name: "Warm Nude", hex: "#C4A484" },
          { name: "Soft Rose Brown", hex: "#B48A7A" },
        ],
        eyeshadows: [
          { name: "Soft Olive", hex: "#9A9A74" },
          { name: "Warm Taupe", hex: "#A89A84" },
          { name: "Muted Teal", hex: "#6A8A84" },
          { name: "Soft Bronze", hex: "#A48A6A" },
          { name: "Dusty Plum", hex: "#8A7A7A" },
          { name: "Warm Grey", hex: "#9A9084" },
        ],
        blushes: [
          { name: "Soft Peach", hex: "#D4B4A4" },
          { name: "Dusty Rose", hex: "#C4A4A4" },
          { name: "Warm Nude", hex: "#C4A494" },
        ],
        bronzers: [
          { name: "Soft Bronze", hex: "#A4846A" },
          { name: "Warm Taupe", hex: "#9A8A7A" },
        ],
        metals: {
          best: [
            { name: "Matte Gold", hex: "#D4B896" },
            { name: "Rose Gold", hex: "#C9A090" },
            { name: "Antique Brass", hex: "#A89060" },
          ],
          avoid: [
            { name: "Bright Silver", hex: "#E8E8E8" },
            { name: "Shiny Gold", hex: "#FFD700" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Soft Brown", hex: "#8B7355" },
          { name: "Warm Ash Brown", hex: "#7A6B5B" },
          { name: "Muted Auburn", hex: "#8B5A45" },
          { name: "Soft Caramel", hex: "#A68B5B" },
        ],
        canWork: [
          { name: "Muted Copper", hex: "#9A6A4A" },
          { name: "Soft Blonde", hex: "#C4A484" },
          { name: "Warm Mushroom", hex: "#8A7A6A" },
        ],
        avoid: [
          { name: "Jet Black", hex: "#1C1C1C" },
          { name: "Platinum Blonde", hex: "#E8E4E1" },
          { name: "Bright Copper", hex: "#DA8A67" },
          { name: "Cool Ash", hex: "#A89F91" },
        ],
        highlights: [
          { name: "Soft Gold", hex: "#C4A474" },
          { name: "Warm Blonde", hex: "#C8B08A" },
        ],
        lowlights: [
          { name: "Soft Brown", hex: "#6A5A4A" },
          { name: "Warm Taupe", hex: "#7A6A5A" },
        ],
      },
      tips: [
        "Keep everything muted and soft - avoid anything too bright or stark",
        "Dusty, warm neutrals are your signature look",
        "Matte and satin finishes are more flattering than high shimmer",
        "Your metals should be warm but not too shiny - antique gold is perfect",
        "Earth tones with a soft touch are always your best bet",
      ],
    },
    TRUE: {
      makeup: {
        lipsticks: [
          { name: "Pumpkin", hex: "#C86A3A" },
          { name: "Rust", hex: "#B7572A" },
          { name: "Warm Brick", hex: "#B44A3A" },
          { name: "Terracotta", hex: "#C67450" },
          { name: "Burnt Sienna", hex: "#A45A3A" },
        ],
        eyeshadows: [
          { name: "Olive Green", hex: "#808000" },
          { name: "Warm Bronze", hex: "#A67B4A" },
          { name: "Copper", hex: "#B87333" },
          { name: "Mustard", hex: "#C4A23A" },
          { name: "Warm Brown", hex: "#8B5A2B" },
          { name: "Forest Green", hex: "#4A6A4A" },
        ],
        blushes: [
          { name: "Burnt Peach", hex: "#C4845A" },
          { name: "Terracotta", hex: "#B8745A" },
          { name: "Warm Coral", hex: "#C47A5A" },
        ],
        bronzers: [
          { name: "Warm Bronze", hex: "#A4744A" },
          { name: "Golden Brown", hex: "#8A6A3A" },
        ],
        metals: {
          best: [
            { name: "Yellow Gold", hex: "#D4A43A" },
            { name: "Copper", hex: "#B87333" },
            { name: "Brass", hex: "#B5A642" },
          ],
          avoid: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Platinum", hex: "#E5E4E2" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Warm Auburn", hex: "#8B4513" },
          { name: "Copper Brown", hex: "#8B5A2B" },
          { name: "Chestnut", hex: "#7B4B2A" },
          { name: "Warm Golden Brown", hex: "#8A6A3A" },
        ],
        canWork: [
          { name: "Bright Copper", hex: "#B87333" },
          { name: "Rich Ginger", hex: "#9A4A2A" },
          { name: "Warm Black", hex: "#2C2416" },
        ],
        avoid: [
          { name: "Ash Tones", hex: "#A89F91" },
          { name: "Platinum", hex: "#E8E4E1" },
          { name: "Blue-Black", hex: "#1C2841" },
          { name: "Cool Brown", hex: "#694F3A" },
        ],
        highlights: [
          { name: "Golden Copper", hex: "#C4844A" },
          { name: "Warm Caramel", hex: "#B4843A" },
        ],
        lowlights: [
          { name: "Rich Brown", hex: "#5A3A2A" },
          { name: "Deep Auburn", hex: "#6A3A2A" },
        ],
      },
      tips: [
        "Rich, warm earthy colors are your signature - embrace them fully",
        "Rust, pumpkin, and terracotta lip colors will make you glow",
        "Gold and copper jewelry are your best metals",
        "Olive and forest green eyeshadows complement your warmth beautifully",
        "Avoid anything with cool, blue, or grey undertones",
      ],
    },
    DEEP: {
      makeup: {
        lipsticks: [
          { name: "Deep Berry", hex: "#8B3A4A" },
          { name: "Burgundy", hex: "#722F37" },
          { name: "Warm Brick", hex: "#9A3A2A" },
          { name: "Deep Terracotta", hex: "#8A4A3A" },
          { name: "Rich Plum", hex: "#6A3A4A" },
        ],
        eyeshadows: [
          { name: "Deep Olive", hex: "#5A5A2A" },
          { name: "Chocolate", hex: "#5A3A2A" },
          { name: "Forest Green", hex: "#3A4A3A" },
          { name: "Deep Bronze", hex: "#7A5A3A" },
          { name: "Burgundy", hex: "#6A2A3A" },
          { name: "Deep Teal", hex: "#2A4A4A" },
        ],
        blushes: [
          { name: "Deep Peach", hex: "#B4745A" },
          { name: "Warm Berry", hex: "#9A5A5A" },
          { name: "Rich Terracotta", hex: "#A4644A" },
        ],
        bronzers: [
          { name: "Deep Bronze", hex: "#8A5A3A" },
          { name: "Rich Tan", hex: "#7A5A4A" },
        ],
        metals: {
          best: [
            { name: "Rich Gold", hex: "#C4943A" },
            { name: "Antique Brass", hex: "#8A6A3A" },
            { name: "Bronze", hex: "#8A5A3A" },
          ],
          avoid: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Rose Gold", hex: "#E8B4B8" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Dark Chocolate", hex: "#3C2415" },
          { name: "Deep Auburn", hex: "#5A2A1A" },
          { name: "Rich Espresso", hex: "#2A1A10" },
          { name: "Dark Warm Brown", hex: "#4A3020" },
        ],
        canWork: [
          { name: "Warm Black", hex: "#1C1410" },
          { name: "Deep Chestnut", hex: "#5A3A2A" },
          { name: "Rich Mahogany", hex: "#5A2A20" },
        ],
        avoid: [
          { name: "Blonde", hex: "#E8D4A2" },
          { name: "Ash Tones", hex: "#A89F91" },
          { name: "Cool Black", hex: "#1C1C1C" },
          { name: "Red-Orange", hex: "#C65D3B" },
        ],
        highlights: [
          { name: "Rich Caramel", hex: "#9A6A3A" },
          { name: "Warm Copper", hex: "#8A5A3A" },
        ],
        lowlights: [
          { name: "Deep Espresso", hex: "#2A1A0A" },
          { name: "Warm Black", hex: "#1C1008" },
        ],
      },
      tips: [
        "Rich, deep warm colors create the most impact on you",
        "Burgundy and deep berry lips are stunning on your coloring",
        "You can handle more drama and depth than other Autumns",
        "Gold jewelry in rich, warm tones is most flattering",
        "Avoid light, pastel, or icy colors - they wash you out",
      ],
    },
  },
  WINTER: {
    COOL: {
      makeup: {
        lipsticks: [
          { name: "Icy Pink", hex: "#E8A0B8" },
          { name: "Cool Fuchsia", hex: "#C8509A" },
          { name: "Berry", hex: "#8B3A60" },
          { name: "Blue-Red", hex: "#C41E3A" },
          { name: "Cool Plum", hex: "#7A3A5A" },
        ],
        eyeshadows: [
          { name: "Icy Silver", hex: "#D4D4D8" },
          { name: "Cool Grey", hex: "#8A8A9A" },
          { name: "Navy", hex: "#2A3A5A" },
          { name: "Icy Lavender", hex: "#D8C8E8" },
          { name: "Charcoal", hex: "#3A3A4A" },
          { name: "Cool Plum", hex: "#5A3A5A" },
        ],
        blushes: [
          { name: "Icy Pink", hex: "#E8B0C0" },
          { name: "Cool Rose", hex: "#D8A0B0" },
          { name: "Soft Fuchsia", hex: "#D890A8" },
        ],
        bronzers: [
          { name: "Cool Contour", hex: "#8A7A7A" },
          { name: "Soft Taupe", hex: "#9A8A8A" },
        ],
        metals: {
          best: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Platinum", hex: "#E5E4E2" },
            { name: "White Gold", hex: "#E8E4E1" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Brass", hex: "#B5A642" },
            { name: "Copper", hex: "#B87333" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Blue-Black", hex: "#1C2841" },
          { name: "Cool Dark Brown", hex: "#3A3030" },
          { name: "Icy Platinum", hex: "#E8E4E8" },
          { name: "Cool Espresso", hex: "#2A2020" },
        ],
        canWork: [
          { name: "Silver Grey", hex: "#A8A8B0" },
          { name: "Cool Ash Brown", hex: "#5A5050" },
          { name: "Jet Black", hex: "#1C1C1C" },
        ],
        avoid: [
          { name: "Warm Brown", hex: "#8B5A2B" },
          { name: "Golden Blonde", hex: "#E8D4A2" },
          { name: "Copper", hex: "#B87333" },
          { name: "Auburn", hex: "#A52A2A" },
        ],
        highlights: [
          { name: "Icy Blonde", hex: "#E8E0E8" },
          { name: "Cool Ash", hex: "#C0B8C0" },
        ],
        lowlights: [
          { name: "Cool Black", hex: "#1C1C20" },
          { name: "Deep Espresso", hex: "#2A2028" },
        ],
      },
      tips: [
        "Cool, clear, and icy colors are your superpowers",
        "Silver and platinum jewelry will always outshine gold on you",
        "Blue-based reds and fuchsia lips are incredibly striking",
        "Avoid anything warm, golden, or earthy",
        "High contrast looks (like dark hair with light skin) work beautifully",
      ],
    },
    TRUE: {
      makeup: {
        lipsticks: [
          { name: "True Red", hex: "#C41E3A" },
          { name: "Hot Pink", hex: "#E8408A" },
          { name: "Fuchsia", hex: "#C8308A" },
          { name: "Deep Berry", hex: "#8A2A5A" },
          { name: "Vivid Plum", hex: "#7A2A5A" },
        ],
        eyeshadows: [
          { name: "Black", hex: "#1C1C1C" },
          { name: "Silver", hex: "#C0C0C0" },
          { name: "Royal Blue", hex: "#2A4AB8" },
          { name: "Emerald", hex: "#2A6A4A" },
          { name: "Deep Purple", hex: "#4A2A6A" },
          { name: "Charcoal", hex: "#3A3A3A" },
        ],
        blushes: [
          { name: "Cool Pink", hex: "#E890A8" },
          { name: "Berry", hex: "#C87090" },
          { name: "Vivid Rose", hex: "#D8708A" },
        ],
        bronzers: [
          { name: "Cool Bronze", hex: "#8A7068" },
          { name: "Soft Contour", hex: "#9A8078" },
        ],
        metals: {
          best: [
            { name: "Bright Silver", hex: "#E8E8E8" },
            { name: "Platinum", hex: "#E5E4E2" },
            { name: "White Gold", hex: "#E8E4E1" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Brass", hex: "#B5A642" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Jet Black", hex: "#0A0A0A" },
          { name: "Blue-Black", hex: "#1C2841" },
          { name: "Espresso", hex: "#2A1A18" },
          { name: "Icy White", hex: "#F0F0F0" },
        ],
        canWork: [
          { name: "Cool Dark Brown", hex: "#2A2420" },
          { name: "Silver", hex: "#B0B0B8" },
          { name: "Platinum Blonde", hex: "#E8E4E8" },
        ],
        avoid: [
          { name: "Warm Brown", hex: "#8B5A2B" },
          { name: "Golden Blonde", hex: "#E8D4A2" },
          { name: "Auburn", hex: "#A52A2A" },
          { name: "Caramel", hex: "#C68E5A" },
        ],
        highlights: [
          { name: "Platinum", hex: "#E8E4E8" },
          { name: "Icy Violet", hex: "#D8D0E0" },
        ],
        lowlights: [
          { name: "Jet Black", hex: "#0A0A0A" },
          { name: "Deep Blue-Black", hex: "#1A1A28" },
        ],
      },
      tips: [
        "Bold, vivid, high-contrast looks are made for you",
        "True red lipstick is your signature - own it",
        "Black and white together is striking on your coloring",
        "Silver jewelry is your best metal by far",
        "Never shy away from drama - you can handle it",
      ],
    },
    DEEP: {
      makeup: {
        lipsticks: [
          { name: "Deep Wine", hex: "#5A1A2A" },
          { name: "Burgundy", hex: "#6A2A3A" },
          { name: "Deep Plum", hex: "#4A2A4A" },
          { name: "Rich Berry", hex: "#6A2A4A" },
          { name: "Dark Cherry", hex: "#5A1A3A" },
        ],
        eyeshadows: [
          { name: "Deep Charcoal", hex: "#2A2A2A" },
          { name: "Forest Green", hex: "#2A4A3A" },
          { name: "Deep Navy", hex: "#1A2A4A" },
          { name: "Burgundy", hex: "#5A2A3A" },
          { name: "Deep Bronze", hex: "#5A4A3A" },
          { name: "Aubergine", hex: "#4A2A4A" },
        ],
        blushes: [
          { name: "Deep Rose", hex: "#A86080" },
          { name: "Rich Berry", hex: "#985070" },
          { name: "Plum", hex: "#886078" },
        ],
        bronzers: [
          { name: "Deep Bronze", hex: "#7A5A48" },
          { name: "Rich Contour", hex: "#6A4A3A" },
        ],
        metals: {
          best: [
            { name: "Silver", hex: "#C0C0C0" },
            { name: "Gunmetal", hex: "#8A8A90" },
            { name: "Dark Platinum", hex: "#D0D0D0" },
          ],
          avoid: [
            { name: "Yellow Gold", hex: "#FFD700" },
            { name: "Rose Gold", hex: "#E8B4B8" },
          ],
        },
      },
      hair: {
        ideal: [
          { name: "Deep Espresso", hex: "#1A1008" },
          { name: "Blue-Black", hex: "#1C2030" },
          { name: "Rich Black", hex: "#0A0A0A" },
          { name: "Deep Cool Brown", hex: "#2A2018" },
        ],
        canWork: [
          { name: "Dark Burgundy", hex: "#4A1A2A" },
          { name: "Deep Plum Brown", hex: "#3A2028" },
          { name: "Cool Black", hex: "#0A0A10" },
        ],
        avoid: [
          { name: "Light Brown", hex: "#A67B5B" },
          { name: "Blonde", hex: "#E8D4A2" },
          { name: "Warm Auburn", hex: "#A52A2A" },
          { name: "Copper", hex: "#B87333" },
        ],
        highlights: [
          { name: "Cool Espresso", hex: "#4A3030" },
          { name: "Deep Plum", hex: "#4A2838" },
        ],
        lowlights: [
          { name: "Jet Black", hex: "#0A0A0A" },
          { name: "Deep Blue-Black", hex: "#0A1020" },
        ],
      },
      tips: [
        "Deep, rich, dramatic colors are your strength",
        "Burgundy and wine lip colors are incredibly sophisticated on you",
        "You can wear dark, moody makeup that would overwhelm others",
        "Keep your contrast high - avoid anything too muted or light",
        "Silver and gunmetal jewelry complement your depth beautifully",
      ],
    },
  },
};
